import React, { useEffect, useRef, useState } from "react";
import { Briefcase, FileText, Gauge, CheckCircle2, ArrowRight, RotateCw } from "lucide-react";
import { usePrice } from "@/lib/price";

const stages = [
  { key: "paste", label: "Paste a job URL", icon: Briefcase, color: "bg-[#FFE600]" },
  { key: "parse", label: "Parsed JD → keywords", icon: FileText, color: "bg-[#0055FF]", fg: "text-white" },
  { key: "resume", label: "Tailored resume drafted", icon: FileText, color: "bg-[#FF00FF]", fg: "text-white" },
  { key: "sim", label: "Mock interview scored", icon: Gauge, color: "bg-[#FF3B30]", fg: "text-white" },
  { key: "sent", label: "Application sent", icon: CheckCircle2, color: "bg-[#00FF66]" },
];

export default function AnimatedDemo() {
  const wrap = useRef(null);
  const [stage, setStage] = useState(0);
  const [running, setRunning] = useState(false);
  const { price } = usePrice();

  useEffect(() => {
    const el = wrap.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !running) {
            setRunning(true);
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.35 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [running]);

  useEffect(() => {
    if (!running) return;
    if (stage >= stages.length - 1) return;
    const t = setTimeout(() => setStage((s) => s + 1), 1100);
    return () => clearTimeout(t);
  }, [running, stage]);

  return (
    <section
      id="try"
      data-testid="animated-demo"
      ref={wrap}
      className="relative border-b-4 border-[#0a0a0a] bg-[#0a0a0a] text-[#FDFBF7] overflow-hidden"
    >
      <div className="absolute inset-0 bg-grid opacity-[0.06] pointer-events-none" />
      <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-28">
        <div className="mb-12 md:mb-14 reveal">
          <div className="font-accent text-sm bg-[#FFE600] text-[#0a0a0a] inline-block px-3 py-1 mb-4">
            LIVE DEMO
          </div>
          <h2 className="font-display text-5xl md:text-7xl">
            Paste a job.
            <br />
            <span className="text-[#FFE600]">Watch it move.</span>
          </h2>
          <p className="font-mono-body mt-5 max-w-2xl text-[#FDFBF7]/75 text-base md:text-lg">
            This is the loop the vault runs for you. From a posting URL to a
            scored, interview-ready, sent application — without leaving the tab.
          </p>
        </div>

        <div className="bg-[#FDFBF7] text-[#0a0a0a] border-4 border-[#FDFBF7] shadow-brutal-lg p-5 md:p-8">
          {/* URL mock */}
          <div className="flex items-center gap-3 bg-[#0a0a0a] text-[#FFE600] border-2 border-[#0a0a0a] px-4 py-3">
            <Briefcase size={16} className="shrink-0" />
            <div className="font-mono-body text-[12px] md:text-sm truncate flex-1">
              https://boards.greenhouse.io/stripe/jobs/sr-product-manager-payments
            </div>
            <button
              data-testid="demo-replay"
              onClick={() => {
                setStage(0);
                setRunning(true);
              }}
              className="hidden md:inline-flex items-center gap-1.5 bg-[#FFE600] text-[#0a0a0a] px-2.5 py-1 font-accent text-xs border-2 border-[#FFE600]"
            >
              <RotateCw size={12} strokeWidth={3} /> Replay
            </button>
          </div>

          {/* Pipeline */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-5 gap-3 items-stretch">
            {stages.map((s, i) => {
              const Icon = s.icon;
              const done = i < stage;
              const active = i === stage;
              return (
                <React.Fragment key={s.key}>
                  <div
                    data-testid={`demo-step-${i}`}
                    className={`border-4 border-[#0a0a0a] p-4 transition-all duration-500 ${
                      done
                        ? "bg-[#00FF66]"
                        : active
                          ? `${s.color} ${s.fg || ""}`
                          : "bg-white"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Icon size={18} strokeWidth={2.5} />
                      <span className="font-accent text-[10.5px]">
                        STEP 0{i + 1}
                      </span>
                    </div>
                    <div className="mt-2 font-display text-[15px] leading-tight">
                      {s.label}
                    </div>
                    {active && (
                      <div className="mt-3 h-2 bg-[#0a0a0a]/15 overflow-hidden">
                        <div
                          className="h-full bg-[#0a0a0a] animate-pulse"
                          style={{ width: "65%" }}
                        />
                      </div>
                    )}
                    {done && (
                      <div className="mt-2 inline-flex items-center gap-1 font-accent text-[11px]">
                        <CheckCircle2 size={12} strokeWidth={3} /> DONE
                      </div>
                    )}
                  </div>
                  {i < stages.length - 1 && (
                    <div className="hidden md:flex items-center justify-center -mx-1.5">
                      <ArrowRight size={20} strokeWidth={3} />
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>

          <div
            data-testid="demo-results"
            className="mt-7 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 border-t-4 border-[#0a0a0a] pt-5"
          >
            <div className="font-mono-body text-[13px]">
              <span className="text-[#00FF66] font-bold">●</span> Interview
              Score:{" "}
              <span className="font-bold">{stage >= 3 ? "92 / 100" : "—"}</span>{" "}
              · Resume ATS Match:{" "}
              <span className="font-bold">{stage >= 2 ? "97%" : "—"}</span> ·
              Status:{" "}
              <span className="font-bold">
                {stage >= 4 ? "Sent" : "In flight"}
              </span>
            </div>
            <a
              href="https://whop.com/checkout/plan_hfsvR7lsOjQds"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="demo-cta"
              className="hover-pop inline-flex items-center justify-center gap-2 bg-[#FFE600] text-[#0a0a0a] border-4 border-[#0a0a0a] px-5 py-3 font-accent text-sm md:text-base shadow-brutal-sm"
            >
              Run this on a real job — {price}
              <ArrowRight size={16} strokeWidth={3} />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
